import { AppBar, Grid, Toolbar, useTheme } from "@mui/material";
import {
	BackdropCircularProgress,
	EmptyList,
	EnhancedSnackbar,
} from "@websential/cosmic-v20";
import {
	EMPLOYEE_LIST_SCREEN_SLUG,
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../../common/constants";
import { FormProvider, useForm } from "react-hook-form";
import { GET_QUERY_KEY, QUERY_OPTIONS } from "../../../common/constants";
import { IResponse, IServerError } from "../../../models/common";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {

	formatDate,
	useNavigateToLogout,
	useRemoveQueriesOnUnmount,
	sortDDLByLabel,
} from "../../../common/Common";
import {
	setSelectedActivityLogActions,
	setSelectedStickyNavBar,
} from "../../../store/actions";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AuthService } from "../../../services";
import { CompanyTD1FormService } from "../../../services/CompanyTD1FormService";
import { DateRange } from "@mui/icons-material";
import { EmployeeService } from "../../../services/EmployeeService";
import FileSaver from "file-saver";
import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { IGetListRequest } from "../../../models";
import { LookupFieldService } from "../../../services/LookupFieldService";
import { ReportsService } from "../../../services/ReportsService";
import { TimeSheetReportDataGridWithColumnToggled } from "./TimeSheetReportDataGridWithColumnToggled";
import { TimeSheetReportHeader } from "./TimeSheetReportHeader";
import { endOfDay } from "date-fns";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { useNavigate } from "react-router-dom";

export interface ITimeSheetReportSetupPage { }
const TIME_SHEET_REPORT_QUERY_KEY = "time-sheet-report";
const PAY_PERIOD_DDL_DATA_QUERY_KEY = "pay-period-ddl-record";


const useTimeSheetReport = (
	runGetListAPICall: boolean,
	clientId: string,
	taxYear: number,
	employee: string,
	hourType: string,
	startDate: string | null,
	endDate: string | null,
	payPeriod: string,
	// classes: string,
	params: IGetListRequest,
	navigateToLogout: (path: any) => void
) =>
	useQuery({
		queryKey: [
			TIME_SHEET_REPORT_QUERY_KEY,
			runGetListAPICall,
			clientId,
			taxYear,
			employee,
			hourType,
			startDate,
			endDate,
			payPeriod,
			// classes,
			params,
		],
		queryFn: () =>
			ReportsService.getTimeSheetReport(
				runGetListAPICall,
				clientId,
				taxYear,
				employee,
				hourType,
				startDate,
				endDate,
				payPeriod,
				// classes,
				params,
				navigateToLogout
			),
		refetchOnWindowFocus: false,
	});

const useGetEmployeeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["Employee-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});
const useGetHourTypeDDL = (clientId: string) =>
	useQuery({
		queryKey: ["Hour-Type-ddl", clientId],
		queryFn: () => {
			return EmployeeService.getEmployeeDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});
const useGetPayFrequencyDDL = (clientId: string, taxYear: number) =>
	useQuery({
		queryKey: ["pay-frequency-ddl"],
		queryFn: (data) => {
			return EmployeeService.getPayFrequencyDDL(clientId, taxYear);
		},
		refetchOnWindowFocus: false,
	});
export const TimeSheetReport: React.FC<ITimeSheetReportSetupPage> = () => {
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const clientIdStore = useSelector((state: IAppState) => state.client.id);
	const clientName = useSelector((state: IAppState) => state.client.clientName);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const taxYear = useSelector((state: IAppState) => state.taxYear.year);
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const navigateToLogout = useNavigateToLogout();

	const queryClient = useQueryClient();
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const [isLoading, setIsLoading] = useState(false);
	const [timeSheetReportData, setTimeSheetReportData] =
		useState<any>(undefined);
	const [runGetListAPICall, setRunGetListAPICall] =
		useState<any>(true);
	const [employeeStatus, setEmployeeStatus] = useState<number>(100);



	const formMethods = useForm<any>({
		mode: "onBlur",
		reValidateMode: "onChange",
	});

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "employeeName",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const employeeId = formMethods.getValues("employee");
	const hourType = formMethods.getValues("hourType");
	const startDate = formMethods.getValues("startDate");
	const endDate = formMethods.getValues("endDate");
	const payPeriod = formMethods.getValues("payPeriod");
	// const classes = formMethods.getValues("classes");

	const { isPending, data } = useTimeSheetReport(
		runGetListAPICall,
		clientId,
		taxYear,
		employeeId && employeeId,
		hourType,
		formatDate(startDate),
		formatDate(endDate),
		payPeriod,
		// classes,
		queryParams,
		navigateToLogout
	);
	const timeSheetList: any | null = data;
	useEffect(() => {
		if (timeSheetList) {
			setTimeSheetReportData(timeSheetList);
		}
	}, [timeSheetList]);

	const { isLoading: isPendingEmployeeDDL, data: employeeDDL } = useGetEmployeeDDL(clientId);
	const { isLoading: isPendingHourTypeDDL, data: hourTypeDDl } = useGetHourTypeDDL(clientId);
	const { isPending: isPendingPayFrequencyDDL, data: payFrequencyDDLItems } =
		useGetPayFrequencyDDL(clientId, taxYear);

	//   after click apply button api calls with updated values and update time sheet report data
	const timeSheetReportDataAPICall = (
		employee: string,
		hourType: string,
		startDate: string | null,
		endDate: string | null,
		payPeriod: string,
		// classes: string,
	) => {
		const fetchData = async () => {
			setIsLoading(true);

			try {
				const response: any = await ReportsService.getTimeSheetReport(
					true,
					clientId,
					taxYear,
					employee,
					hourType,
					formatDate(startDate),
					formatDate(endDate),
					payPeriod,
					// classes,
					queryParams,
					navigateToLogout
				);

				if (response) {
					setTimeSheetReportData(response);
				}
			} catch {
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	};

	const onSubmit = (values: any) => {

		timeSheetReportDataAPICall(
			values.employee,
			values.hourType,
			values.startDate,
			values.endDate,
			values.payPeriod,
			// values.classes,
		);
	};

	const [zeroEmployee, setzeroEmployee] = useState(true);

	const [submitAddRemoveColumnsHandler, setSubmitAddRemoveColumnsHandler] =
		useState(undefined);
	const [onColumnReorder, setOnColumnReorder] = useState(undefined);

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null
					? requestParams.sortField
					: "employeeName",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
		setRunGetListAPICall(true)
	}, []);

	const {
		isPending: isSubmittingAddRemoveHeaderColumns,
		mutate: submitAddRemoveHeaderColumns,
	} = useMutation({
		mutationFn: ReportsService.getTimeSheetAddRemoveHeaderColumns,
		onSuccess: (res: any | IServerError | IResponse) => {
			if (res.status === 200) {
				setRunGetListAPICall(true)
				return queryClient.invalidateQueries({
					queryKey: [TIME_SHEET_REPORT_QUERY_KEY],
				});
			} else {
				setSnackbarData({
					open: true,
					message: res.message
						? res.message
						: "Something went wrong, try again later!",
					severity: "error",
				});
			}
		},
		onError: (err: any) => {
			setSnackbarData({
				open: true,
				message: err.message,
				severity: "error",
			});
		},
	});


	useEffect(() => {
		if (submitAddRemoveColumnsHandler) {
			submitAddRemoveHeaderColumns({
				clientId: clientId,
				columns: submitAddRemoveColumnsHandler,
				navigateToLogout: navigateToLogout,
			});
		}
	}, [submitAddRemoveColumnsHandler]);

	const employeeDDLListItems = useMemo(() => {
		if (employeeDDL) {
			if (
				employeeDDL &&
				Array.isArray(employeeDDL)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(employeeDDL).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [employeeDDL]);

	const hourTypeDDLListItems = useMemo(() => {
		if (hourTypeDDl) {
			if (
				hourTypeDDl &&
				Array.isArray(hourTypeDDl)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(hourTypeDDl).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [hourTypeDDl]);

	const payPeriodFrequencyDDlItems = useMemo(() => {
		if (payFrequencyDDLItems) {
			if (
				payFrequencyDDLItems &&
				Array.isArray(payFrequencyDDLItems)
			) {
				let resources: IDropDownListResponse[] =
					sortDDLByLabel(payFrequencyDDLItems).map(
						(resourceItem: IDropDownListResponse) => ({
							// id: resourceItem.id,
							label: resourceItem.label,
							value: resourceItem.value,
						})
					);

				resources.unshift({
					label: "All",
					value: "all",
				});

				return resources;
			}
			return [];
		}
	}, [payFrequencyDDLItems]);

	const onSnackbarClosed = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	useRemoveQueriesOnUnmount(["time-sheet-report"]);

	const updatedTimeSheetListItems = useMemo(() => {
		return (
			timeSheetReportData &&
			timeSheetReportData.items &&
			timeSheetReportData?.items.map((item: any) => ({
				...item,
			}))
		);
	}, [timeSheetReportData]);

	const onEmployeeStatusSelectionChangeHandler = (event: any) => {
		setRunGetListAPICall(false)
		formMethods.setValue('employee', "all")
		setEmployeeStatus(event.value);
		// setSelectedEmployee(null);
		// formInitialState();
		formMethods.setValue('employeeStatus', event.value)
	};


	// getting latest updated values of form fields using watch method
	const { watch } = formMethods;
	const values = watch([

		"employeeId",
		"hourType",
		"startDate",

		"endDate",
		"payPeriod",

	]);

	const savePdfAsFile = (xmlData: any, filename: any) => {
		const blob = new Blob([xmlData], { type: "pdf;charset=utf-8" });
		FileSaver.saveAs(blob, filename);
	};

	const onExportButtonClick = async (selectedOption: string) => {
		const [employeeId, hourType, startDate, endDate, payPeriod] = values;
		try {
			setIsLoading(true);
			const response = await ReportsService.exportTimeSheetReport(
				selectedOption,
				clientId,
				employeeId,
				hourType,
				formatDate(startDate),
				formatDate(endDate),
				payPeriod,
				queryParams
			);

			if (response) {
				if (selectedOption === "PDF") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName}-employee.pdf`;
						savePdfAsFile(response, filename);
					}
				} else if (selectedOption === "XLS") {
					if (response.status === 400) {
						setSnackbarData({
							open: true,
							message: response.data.message
								? response.data.message
								: "Something went wrong.",

							severity: "error",
						});
					} else {
						const filename = `${clientName}-employee.xlsx`;
						savePdfAsFile(response, filename);
					}
				} else {
					const fileType =
						selectedOption === "XLS"
							? "application/vnd.ms-excel"
							: "text/csv;charset=utf-8;";
					const fileExtension = selectedOption === "XLS" ? "xls" : "csv";

					const blob = new Blob([response], { type: fileType });
					FileSaver.saveAs(blob, `${clientName}-timeSheet.${fileExtension}`);
				}
			} else {
				console.error("Error: No data received from the server");
			}
		} catch (err) {

			setIsLoading(false);
		} finally {
			setIsLoading(false);
		}

	};


	// const expandedLabels = ["Reports", "Payroll"];
	// const expandedTimeSheetListOrSetupItem = "Time Sheet";
	// const enhanceData = (data: any[], parentLabel?: string): any[] => {
	// 	return data.map((item) => {
	// 		let isExpanded = expandedLabels.includes(item.label);
	// 		if (
	// 			parentLabel === "Payroll" &&
	// 			item.label === expandedTimeSheetListOrSetupItem
	// 		) {
	// 			isExpanded = true;
	// 		}
	// 		const enhancedItem = {
	// 			...item,
	// 			expanded: item.items ? isExpanded : false,
	// 			items: item.items ? enhanceData(item.items, item.label) : undefined,
	// 			path: item.label === expandedTimeSheetListOrSetupItem && "/reports/timesheet",
	// 		};
	// 		return enhancedItem;
	// 	});
	// };

	const expandedLabels = ["Reports"];
	const enhanceData = (data: any[], ancestorChain: string[] = []): any[] => {
		return data.map((item) => {
			const currentChain = [...ancestorChain, item.label];
			const isPayrollUnderReports =
				item.label === "Payroll" && ancestorChain.includes("Reports");
			const expandedPayrollReportOrSetupItem =
				item.label === "Time Sheet" &&
				ancestorChain.includes("Reports") &&
				ancestorChain.includes("Payroll");

			const isExpanded =
				expandedLabels.includes(item.label) ||
				isPayrollUnderReports ||
				expandedPayrollReportOrSetupItem;

			return {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items
					? enhanceData(item.items, currentChain)
					: undefined,
				path: expandedPayrollReportOrSetupItem && expandedPayrollReportOrSetupItem
					? "/reports/timesheet"
					: item.path ?? undefined,
			};
		});
	};
	useEffect(() => {
		dispatch(
			setSelectedActivityLogActions({
				entityId1: "",
				entityId2: "",
				entityId3: "",
				screenName: EMPLOYEE_LIST_SCREEN_SLUG,
				accountantName: "",
				activityType: "activity_type=delete",
				activityLogVisibility: true,
			})
		);
		dispatch(setSelectedStickyNavBar({ isSticky: true }));

		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
		dispatch(setSelectedStickyNavBar({ isSticky: false }));
	}, [drawerMenuOptions]);


	const image =
		"https://taxslips.blob.core.windows.net/taxslips-public-images-container/empty_employee_list.png";

	return (
		<>

			<div
				style={{
					margin: "15px 0",
					fontSize: "1.5rem",
					fontWeight: "bold",
					fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
				}}
			>
				{"Time Sheet"}
			</div>

			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<TimeSheetReportHeader
						employeeDDLListItems={employeeDDLListItems}
						payPeriodListItems={payPeriodFrequencyDDlItems}
						// classesDDLList={classesDDLList}
						setStateRunGetListAPICall={(value: boolean) => setRunGetListAPICall(value)}
					// onApplyButtonClickedHandler={onApplyButtonClickedHandler}
					/>
				</form>
			</FormProvider>

			<Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
				<BackdropCircularProgress
					open={
						isPending ||
						isSubmittingAddRemoveHeaderColumns ||
						isLoading || isPendingEmployeeDDL || isPendingPayFrequencyDDL || isPendingHourTypeDDL
						// isPendingEmployeeDDLItems
					}
					testId="time-sheet-report"
				/>

				<TimeSheetReportDataGridWithColumnToggled
					columns={
						timeSheetReportData?.headerColumns
							? timeSheetReportData.headerColumns.filter(
								(item: any) => item.field !== "id"
							)
							: []
						// timeSheetReportData?.headerColumns ?? []
					}
					completeHeaderColumnsList={
						timeSheetReportData
							&& timeSheetReportData
								?.totalHeaderColumns &&
							Object.keys(timeSheetReportData.totalHeaderColumns).length !== 0
							? timeSheetReportData.totalHeaderColumns
							: {}
						// timeSheetReportData?.headerColumns ?? []
					}
					submitAddRemoveColumnsHandler={(value: any) =>
						setSubmitAddRemoveColumnsHandler(value)
					}
					onColumnReorder={(value: any) => setOnColumnReorder(value)}
					rows={
						timeSheetReportData && updatedTimeSheetListItems
							? updatedTimeSheetListItems
							: []
						// timeSheetReportData?.items ?? []
						// itams
					}
					showIndexColumn={true}
					showChecboxColumn={false}
					showSettingColumn={false}
					// onImportButtonClickedHandler={onImportFileClicked}
					// onImportExcelButtonClickedHandler={onExcelToPdfClicked}
					onSnackbarMessageData={(value: any) => setSnackbarData(value)}
					totalRecords={
						timeSheetReportData && timeSheetReportData.total
							? timeSheetReportData.total
							: 0
					}
					initialPageSize={INITIAL_PAGE_SIZE}
					rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
					onRequestParamsChange={onRequestParamsChange}
					searchFieldPlaceholder={"Search by Name"}
					changeLoginForGetEmptyItemsBySearchQuery={(value: any) =>
						setzeroEmployee(value)
					}
					pageTitle={"Time Sheet"}
					onExportButtonClick={onExportButtonClick}
				/>

				{timeSheetReportData ? (
					<AppBar
						position="sticky"
						color="default"
						elevation={1}
						sx={{
							backgroundColor: "#F7F7F7",

							boxShadow: "none",
							alignItems: "center",
							zIndex: 0,
							position: "sticky",
							bottom: 0,
						}}
					></AppBar>
				) : (
					<></>
				)}
			</Grid>

			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={`snackbar`}
				open={snackbarData.open}
			/>
		</>
	);
};
